export const TRANSITIONS = {
    /**
     * A proposal.
     *
     * `sent` is the moment it becomes a thing the customer can refer to, so that
     * is where it gets its number and where a reservation — if the operator asked
     * for one — takes hold. `accepted` is the customer saying yes; `converted` is
     * what happens to it when the order is created from it.
     */
    quote: {
        draft: ['sent', 'cancelled'],
        sent: ['accepted', 'rejected', 'expired', 'cancelled'],
        accepted: ['converted', 'expired', 'cancelled'],
        // Terminal. A rejected proposal is re-quoted as a new document, because a
        // proposal the customer already turned down is evidence, not a draft.
        rejected: [],
        expired: [],
        converted: [],
        cancelled: [],
    },
    /**
     * A commitment to deliver.
     *
     * `confirmed` numbers it and reserves stock when the document says to;
     * `fulfilled` is the goods leaving, which is what actually consumes stock.
     */
    order: {
        draft: ['confirmed', 'cancelled'],
        confirmed: ['fulfilled', 'cancelled'],
        fulfilled: ['cancelled'],
        cancelled: [],
    },
    /**
     * A transaction that is over by the time it is recorded. The shop, and the
     * till.
     *
     * `returned` appears in ADR-0017's sketch and is deliberately absent here:
     * returns and exchanges are their own feature, and a status with no behaviour
     * behind it is a promise the product does not keep. Reversing a completed
     * sale today is `cancelled`, which writes the movements that give the stock
     * back.
     */
    sale: {
        draft: ['completed', 'cancelled'],
        completed: ['cancelled'],
        cancelled: [],
    },
};
const EFFECT_DEFAULT = {
    allocatesNumber: false,
    stock: 'none',
    closes: false,
};
const EFFECTS = {
    quote: {
        sent: { allocatesNumber: true, stock: 'reserve', closes: false },
        accepted: EFFECT_DEFAULT,
        rejected: { allocatesNumber: false, stock: 'release', closes: true },
        expired: { allocatesNumber: false, stock: 'release', closes: true },
        // The order that was created from it takes the reservation over, so the
        // quote gives its own back rather than the two of them holding the same
        // units twice.
        converted: { allocatesNumber: false, stock: 'release', closes: true },
        cancelled: { allocatesNumber: false, stock: 'reverse', closes: true },
    },
    order: {
        confirmed: { allocatesNumber: true, stock: 'reserve', closes: false },
        fulfilled: { allocatesNumber: false, stock: 'consume', closes: true },
        cancelled: { allocatesNumber: false, stock: 'reverse', closes: true },
    },
    sale: {
        // One transition that numbers it and takes the stock, because a shop sale
        // is not two events. This is the whole of the loja workflow.
        completed: { allocatesNumber: true, stock: 'consume', closes: true },
        cancelled: { allocatesNumber: false, stock: 'reverse', closes: true },
    },
};
/** Which statuses this document may move to right now. Never undefined. */
export const allowedTransitions = (kind, from) => TRANSITIONS[kind][from] ?? [];
export const canTransition = (kind, from, to) => allowedTransitions(kind, from).includes(to);
/**
 * `order: draft → fulfilled` — for a log, an audit row and an error message.
 *
 * English, like the rest of the code. The Portuguese a person reads is built at
 * the API boundary from the same structured refusal, so the two cannot drift
 * into saying different things.
 */
export const describeTransition = (kind, from, to) => `${kind}: ${from} → ${to}`;
export const transition = (kind, from, to) => {
    const allowed = allowedTransitions(kind, from);
    if (!allowed.includes(to)) {
        return { ok: false, refusal: { kind, from, to, allowed } };
    }
    return { ok: true, effect: EFFECTS[kind][to] ?? EFFECT_DEFAULT };
};
/**
 * O efeito de uma transição sob a política de saída da loja.
 *
 * Só um movimento muda de nome: confirmar um pedido reserva por padrão e
 * **consome** quando a loja escolheu `confirmation`. O despacho seguinte não
 * precisa de exceção nenhuma — `applyStockEffect` já não consome duas vezes o
 * que já está consumido, então o mesmo caminho serve para as duas políticas.
 *
 * Aqui e não no repositório porque isto é uma afirmação sobre o domínio, e
 * porque o Edge precisa chegar à mesma conclusão sem perguntar ao servidor.
 */
export const effectUnderPolicy = (kind, to, effect, exitAt) => exitAt === 'confirmation' && kind === 'order' && to === 'confirmed'
    ? { ...effect, stock: 'consume' }
    : effect;
/**
 * The stock state a document holds after an effect has been applied.
 *
 * A pure function of the two, so the column and the ledger cannot drift: there
 * is one place that decides what `reserved` means after a cancellation, and it
 * is testable without a database.
 */
export const nextStockState = (current, effect, reservesStock) => {
    switch (effect) {
        case 'none':
            return current;
        case 'reserve':
            return reservesStock ? 'reserved' : current;
        case 'consume':
            return 'consumed';
        case 'release':
            // Only a live reservation can be released. A document that never held one
            // stays where it was rather than claiming it gave something back.
            return current === 'reserved' ? 'released' : current;
        case 'reverse':
            if (current === 'reserved')
                return 'released';
            if (current === 'consumed')
                return 'reversed';
            return current;
    }
};
/**
 * Whether a document in this state may still have its lines edited.
 *
 * Only a draft. A confirmed order whose lines can still change is an order
 * nobody can rely on — the customer agreed to a set of lines and a total, and
 * editing them afterwards is a new agreement, not an edit.
 */
export const acceptsLineEdits = (status) => status === 'draft';
//# sourceMappingURL=transitions.js.map